import { useEffect } from 'react';
import './CEPortfolioReport.css';
import { CE_TIERS } from './calcEngine';

function num(v) {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
}

function fmtDM(v) {
  if (v == null) return '—';
  if (Math.abs(v) >= 1_000_000) return '$' + (v / 1_000_000).toFixed(2) + 'M';
  return '$' + Math.round(v).toLocaleString();
}

function printDate() {
  return new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

// ── Legacy fund set (pre-tier scenarios only — includes SLVO, 11% planning rate). ──
const FUNDS = [
  {
    ticker: 'FEPI',
    name: 'REX FANG & Innovation Equity Premium Income ETF',
    strategy: 'Holds the "Magnificent 7" mega-cap tech stocks — Apple, NVIDIA, Microsoft, Amazon, Meta, Tesla, and Alphabet — while selling covered calls against them each month. The extreme volatility of these names translates into high premium income without giving up the underlying shares.',
    ttmYield: '25.6%',
    sinceInception: '21.0%',
    inceptionDate: 'Oct 2023',
    alloc: 25,
  },
  {
    ticker: 'QYLD',
    name: 'Global X Nasdaq-100 Covered Call ETF',
    strategy: 'One of the largest and most established covered-call ETFs with over $8 billion in assets. Holds the Nasdaq-100 index and sells at-the-money calls monthly — a workhorse for consistent monthly cash flow since 2013.',
    ttmYield: '12.2%',
    sinceInception: '8.6%',
    inceptionDate: 'Dec 2013',
    alloc: 25,
    context: 'Since-inception return reflects a period when the S&P 500 outperformed the Nasdaq over the long run. Current 12-month distribution rate has improved as tech has reasserted leadership over the broader market.',
  },
  {
    ticker: 'SPYI',
    name: 'NEOS S&P 500 High Income ETF',
    strategy: 'S&P 500 exposure with a monthly options overlay targeting substantially higher income than a traditional index fund. Distributions may qualify for favorable Section 1256 tax treatment in taxable accounts.',
    ttmYield: '12.0%',
    sinceInception: '14.9%',
    inceptionDate: 'Aug 2022',
    alloc: 25,
  },
  {
    ticker: 'SLVO',
    name: 'UBS ETRACS Silver Covered Call ETN',
    strategy: 'Holds silver and sells covered calls against it monthly. Silver\'s elevated volatility produces higher option premiums and higher income. Note: SLVO is an ETN — not an ETF — and carries both commodity and credit risk.',
    ttmYield: '42.3%',
    sinceInception: '4.5%',
    inceptionDate: 'Apr 2013',
    alloc: 25,
    context: "Since-inception return reflects silver's long-term volatility. Current distributions are elevated due to historically high silver prices — this is actively managed and changes with market conditions.",
  },
];

// ── Tiered ETF sleeve (Aug 2026 — SLVO retired, MLPD added). UPDATE MONTHLY ON THE 5TH. ──
// Data as of Aug 4, 2026.
const TIER_ETFS = [
  {
    ticker: 'QYLD',
    name: 'Global X Nasdaq-100 Covered Call ETF',
    strategy: 'One of the largest and most established covered-call ETFs with over $8 billion in assets. Holds the Nasdaq-100 index and sells at-the-money calls monthly — a workhorse for consistent monthly cash flow since 2013.',
    ttmYield: '12.4%',
    inceptionDate: 'Dec 2013',
  },
  {
    ticker: 'SPYI',
    name: 'NEOS S&P 500 High Income ETF',
    strategy: 'S&P 500 exposure with a monthly options overlay targeting substantially higher income than a traditional index fund. Distributions may qualify for favorable Section 1256 tax treatment in taxable accounts.',
    ttmYield: '11.9%',
    inceptionDate: 'Aug 2022',
  },
  {
    ticker: 'MLPD',
    name: 'Global X MLP & Energy Infrastructure Covered Call ETF',
    strategy: 'Holds midstream energy infrastructure — pipelines and processing — via MLPX and sells covered calls against it monthly. Energy infrastructure income is driven by fee-based contracts, adding a sleeve that is not tied to the tech-heavy indexes.',
    ttmYield: '13.9%',
    inceptionDate: 'May 2024',
    context: 'MLPD diversifies the sleeve away from equity-index correlation — its income comes from energy infrastructure, not the Nasdaq or S&P.',
  },
  {
    ticker: 'FEPI',
    name: 'REX FANG & Innovation Equity Premium Income ETF',
    strategy: 'Holds the "Magnificent 7" mega-cap tech stocks while selling covered calls against them each month. The elevated volatility of these names translates into high premium income without giving up the underlying shares.',
    ttmYield: '25.2%',
    inceptionDate: 'Oct 2023',
  },
];

// Anchor structured note — indicative terms (SN wholesaler sheet, Aug 4 2026).
const ANCHOR_NOTE = {
  title: 'Defined-Income Structured Notes',
  underliers: 'S&P 500 / Nasdaq-100 (worst-of)',
  coupon: '9.75%',
  couponBarrier: '75% — with Memory',
  principalBarrier: '60% at maturity',
  term: '36 months, laddered',
  call: 'Issuer callable (no-call 3 months)',
  frequency: 'Monthly coupons',
};

function CEHeader({ clientName }) {
  return (
    <div className="ce-hdr">
      <div className="ce-hdr-left">
        <div className="ce-hdr-logo">JBF</div>
        <span className="ce-hdr-brand">J Bradin Financial</span>
      </div>
      <div className="ce-hdr-right">
        <span className="ce-hdr-client">{clientName}</span>
        <span className="ce-hdr-doc">JBF Convert Engine Portfolio</span>
      </div>
    </div>
  );
}

function CEFooter({ pageNum }) {
  return (
    <div className="ce-ftr">
      <span>J Bradin Financial — Confidential</span>
      <span>Page {pageNum} of 4</span>
    </div>
  );
}

function CEPage({ num: pageNum, clientName, cover, children }) {
  if (cover) return <div className="ce-page ce-cover-page">{children}</div>;
  return (
    <div className="ce-page">
      <CEHeader clientName={clientName} />
      <div className="ce-body">{children}</div>
      <CEFooter pageNum={pageNum} />
    </div>
  );
}

function CESectionTitle({ children }) {
  return <div className="ce-section-title">{children}</div>;
}

// ── The structured-note card (tiered portfolios). ──
function NoteCard({ allocPct, engineSize }) {
  return (
    <div className="ce-note-card">
      <div className="ce-note-card-hdr">
        <span className="ce-note-title">{ANCHOR_NOTE.title}</span>
        <span className="ce-note-alloc">{allocPct}% of Engine{engineSize > 0 ? ` · ${fmtDM(engineSize * allocPct / 100)}` : ''}</span>
      </div>
      <div className="ce-note-grid">
        <div><div className="ce-stat-label">Underliers</div><div className="ce-stat-val-sm">{ANCHOR_NOTE.underliers}</div></div>
        <div><div className="ce-stat-label">Indicative Coupon</div><div className="ce-stat-val">{ANCHOR_NOTE.coupon}</div></div>
        <div><div className="ce-stat-label">Coupon Barrier</div><div className="ce-stat-val-sm">{ANCHOR_NOTE.couponBarrier}</div></div>
        <div><div className="ce-stat-label">Principal Barrier</div><div className="ce-stat-val-sm">{ANCHOR_NOTE.principalBarrier}</div></div>
        <div><div className="ce-stat-label">Term</div><div className="ce-stat-val-sm">{ANCHOR_NOTE.term}</div></div>
        <div><div className="ce-stat-label">Coupons</div><div className="ce-stat-val-sm">{ANCHOR_NOTE.frequency}</div></div>
      </div>
      <div className="ce-note-explain">
        <strong>How the barriers protect you:</strong> coupons pay every month the indexes stay above 75% of their
        starting level — and with the <strong>Memory feature</strong>, any missed coupon is paid back the moment the barrier
        is recovered. Principal is returned in full at maturity unless the worst-performing index has fallen more than
        40%. Notes are laddered across maturities so a portion matures and reinvests on a rolling basis.
      </div>
    </div>
  );
}

export function CEPortfolioReport({ form, results, onClose }) {
  useEffect(() => {
    document.body.classList.add('ce-report-active');
    const t = setTimeout(() => window.print(), 500);
    return () => {
      clearTimeout(t);
      document.body.classList.remove('ce-report-active');
    };
  }, []);

  const clientName = [form.clientFirstName, form.clientLastName].filter(Boolean).join(' ') || 'Client';
  const jbfScenario = form.rothScenarios.find(s => s.vehicle === 'jbf');
  const tier = CE_TIERS[jbfScenario?.tier] || null;   // null ⇒ legacy pre-tier report
  const planRate = tier ? tier.planningRate : 0.11;
  const planPct = Math.round(planRate * 100);
  const ceTarget   = num(jbfScenario?.ceTargetSize);
  const year1Amt   = num(jbfScenario?.year1Amount) || num(jbfScenario?.conversionAmount);
  const engineSize = ceTarget > 0 ? ceTarget : year1Amt;
  const annualDist = engineSize * planRate;
  const monthlyDist = annualDist / 12;
  const jbfExtras = jbfScenario ? results?.v2JBFExtras?.[jbfScenario.id] : null;
  const ceFundedYear = jbfExtras?.ceFundedYear ?? null;
  const ceFundedAge  = jbfExtras?.ceFundedAge ?? null;

  const snAlloc  = tier ? Math.round(tier.snPct * 100) : 0;
  const etfAlloc = tier ? Math.round(tier.etfPct * 100) : 0;
  const perEtf   = etfAlloc > 0 ? etfAlloc / TIER_ETFS.length : 0;

  return (
    <div>
      {/* Screen-only close bar */}
      <div className="ce-close-bar">
        <div className="ce-close-bar-left">
          <div className="ce-hdr-logo" style={{ width: 32, height: 32, fontSize: '0.65rem' }}>JBF</div>
          <span style={{ color: 'white', fontWeight: 700, fontSize: '0.95rem' }}>CE Portfolio Report — Print Preview</span>
        </div>
        <button className="ce-close-btn" onClick={onClose}>Close</button>
      </div>

      <div className="ce-pages-wrap">

        {/* ── PAGE 1: COVER ── */}
        <CEPage num={1} clientName={clientName} cover>
          <div className="ce-cover-header-bar">
            <div className="ce-cover-logo">JBF</div>
            <div className="ce-cover-firm-header">J Bradin Financial</div>
          </div>
          <div className="ce-cover-inner">
            <div className="ce-cover-top-rule" />
            <div className="ce-cover-mid">
              <div className="ce-cover-doc-label">Your JBF Convert Engine Portfolio</div>
              <div className="ce-cover-names">{clientName}</div>
              {tier && (
                <div className="ce-cover-tier">{tier.label} · {tier.sub}</div>
              )}
            </div>
            <div className="ce-cover-divider" />
            <div className="ce-cover-meta">
              <div className="ce-cover-meta-row">
                <span className="ce-cover-meta-label">Prepared by</span>
                <span className="ce-cover-meta-value">Jesse Bradin</span>
              </div>
              <div className="ce-cover-meta-row">
                <span className="ce-cover-meta-label">Date Prepared</span>
                <span className="ce-cover-meta-value">{printDate()}</span>
              </div>
              {engineSize > 0 && (
                <div className="ce-cover-meta-row">
                  <span className="ce-cover-meta-label">Engine Size</span>
                  <span className="ce-cover-meta-value">{fmtDM(engineSize)}</span>
                </div>
              )}
            </div>
            {ceTarget > 0 && ceFundedYear && (
              <div style={{ fontSize: '12px', color: '#1B2B5E', marginTop: '12px', fontStyle: 'italic', textAlign: 'center' }}>
                Your engine reaches its full {fmtDM(engineSize)} size by Year {ceFundedYear}{ceFundedAge != null ? ` (age ${ceFundedAge})` : ''}.
              </div>
            )}
            <div className="ce-cover-tagline">
              "The Convert Engine pays its own taxes — every year, after Year 1."
            </div>
          </div>
          <div className="ce-cover-footer">
            <span>J Bradin Financial — Confidential</span>
            <span>Page 1 of 4</span>
          </div>
        </CEPage>

        {/* ── PAGE 2: THE PORTFOLIO ── */}
        <CEPage num={2} clientName={clientName}>
          {tier ? (
            <>
              <CESectionTitle>Your Income Portfolio — {tier.label}</CESectionTitle>
              {snAlloc > 0 && <NoteCard allocPct={snAlloc} engineSize={engineSize} />}
              {etfAlloc > 0 && (
                <>
                  <CESectionTitle>The Income ETF Sleeve — {etfAlloc}% of Engine</CESectionTitle>
                  <div className="ce-fund-grid">
                    {TIER_ETFS.map(fund => (
                      <div className="ce-fund-card" key={fund.ticker}>
                        <div className="ce-fund-card-hdr">
                          <div className="ce-fund-ticker">{fund.ticker}</div>
                        </div>
                        <div className="ce-fund-body">
                          <div className="ce-fund-name">{fund.name}</div>
                          <div className="ce-fund-strategy">{fund.strategy}</div>
                          <div className="ce-fund-stats">
                            <div>
                              <div className="ce-stat-label">12-Mo Distribution</div>
                              <div className="ce-stat-val">{fund.ttmYield}</div>
                              <div style={{ fontSize: '9px', color: '#9ca3af', marginTop: '1px' }}>{fund.inceptionDate}</div>
                            </div>
                            <div>
                              <div className="ce-stat-label">Allocation</div>
                              <div className="ce-stat-val">{perEtf}%</div>
                            </div>
                            {engineSize > 0 && (
                              <div>
                                <div className="ce-stat-label">Dollar Amount</div>
                                <div className="ce-stat-val">{fmtDM(engineSize * perEtf / 100)}</div>
                              </div>
                            )}
                          </div>
                          {fund.context && (
                            <div style={{ fontSize: '10px', color: '#64748b', fontStyle: 'italic', marginTop: '4px', lineHeight: 1.5 }}>
                              {fund.context}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {etfAlloc === 0 && (
                <div className="ce-note-solo-box">
                  <p>Your risk number placed you in the most defined version of the Convert Engine: <strong>100% structured
                  notes</strong>. Every dollar of the engine sits behind the 75% coupon barrier with memory and the 60%
                  principal barrier — the {planPct}% planning rate is set deliberately below the {ANCHOR_NOTE.coupon} indicative
                  coupon, so the plan carries a built-in cushion.</p>
                </div>
              )}
            </>
          ) : (
            <>
              <CESectionTitle>The Four Funds</CESectionTitle>
              <div className="ce-fund-grid">
                {FUNDS.map(fund => (
                  <div className="ce-fund-card" key={fund.ticker}>
                    <div className="ce-fund-card-hdr">
                      <div className="ce-fund-ticker">{fund.ticker}</div>
                    </div>
                    <div className="ce-fund-body">
                      <div className="ce-fund-name">{fund.name}</div>
                      <div className="ce-fund-strategy">{fund.strategy}</div>
                      <div className="ce-fund-stats">
                        <div>
                          <div className="ce-stat-label">12-Month Return</div>
                          <div className="ce-stat-val">{fund.ttmYield}</div>
                        </div>
                        <div>
                          <div className="ce-stat-label">Since Inception</div>
                          <div className="ce-stat-val">{fund.sinceInception}</div>
                          <div style={{ fontSize: '9px', color: '#9ca3af', marginTop: '1px' }}>{fund.inceptionDate}</div>
                        </div>
                        <div>
                          <div className="ce-stat-label">Allocation</div>
                          <div className="ce-stat-val">{fund.alloc}%</div>
                        </div>
                        {engineSize > 0 && (
                          <div>
                            <div className="ce-stat-label">Dollar Amount</div>
                            <div className="ce-stat-val">{fmtDM(engineSize * fund.alloc / 100)}</div>
                          </div>
                        )}
                      </div>
                      {fund.context && (
                        <div style={{ fontSize: '10px', color: '#64748b', fontStyle: 'italic', marginTop: '4px', lineHeight: 1.5 }}>
                          {fund.context}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <CESectionTitle>{tier ? 'How the Income Is Generated' : 'How Covered Call Income Works'}</CESectionTitle>
          <div className="ce-how-box">
            <div className="ce-how-steps">
              <div className="ce-how-step">
                <div className="ce-how-icon">🏦</div>
                <div className="ce-how-title">{tier ? 'Defined Coupons' : 'Hold the Asset'}</div>
                <div className="ce-how-desc">
                  {tier
                    ? 'The structured notes pay a set monthly coupon as long as the indexes hold above the 75% barrier — and missed coupons are recovered via the Memory feature.'
                    : 'Each fund holds a portfolio of stocks or commodities — just like a standard ETF. You own a share of those holdings.'}
                </div>
              </div>
              <div className="ce-how-arrow">→</div>
              <div className="ce-how-step">
                <div className="ce-how-icon">📝</div>
                <div className="ce-how-title">{tier ? 'Covered Call Premiums' : 'Sell Call Options'}</div>
                <div className="ce-how-desc">
                  {tier
                    ? (etfAlloc > 0
                      ? 'The ETF sleeve sells monthly call options on its holdings and distributes the cash premium collected.'
                      : 'Coupons arrive monthly, in cash — no options exposure, no fund NAV mechanics inside the engine sleeve.')
                    : 'Each month the fund sells the right for others to buy those assets at a set price. The buyers pay a cash premium upfront for that right.'}
                </div>
              </div>
              <div className="ce-how-arrow">→</div>
              <div className="ce-how-step">
                <div className="ce-how-icon">💰</div>
                <div className="ce-how-title">Fund the Conversion Taxes</div>
                <div className="ce-how-desc">The monthly income accumulates in cash and covers each year's Roth conversion tax bill — so the conversion never takes a check from your savings after Year 1.</div>
              </div>
            </div>
          </div>
        </CEPage>

        {/* ── PAGE 3: PORTFOLIO AT WORK ── */}
        <CEPage num={3} clientName={clientName}>
          <CESectionTitle>Your Portfolio At Work</CESectionTitle>

          {/* Hero */}
          <div className="ce-hero-wrap">
            <div className="ce-hero">
              <div className="ce-hero-label">Engine Size</div>
              <div className="ce-hero-value">{engineSize > 0 ? fmtDM(engineSize) : '—'}</div>
              <div className="ce-hero-sub">{tier ? `${tier.label} — ${tier.sub}` : 'Total invested across all four funds'}</div>
            </div>
          </div>

          {/* Key Metrics Grid */}
          <div className="ce-metrics-grid">
            <div className="ce-metric-card ce-metric-gold">
              <div className="ce-metric-label">Planning Distribution Rate</div>
              <div className="ce-metric-value">{planPct}%</div>
              {tier && <div className="ce-metric-sub">Set below the {ANCHOR_NOTE.coupon} indicative note coupon by design</div>}
            </div>
            <div className="ce-metric-card ce-metric-navy">
              <div className="ce-metric-label">Annual Distribution</div>
              <div className="ce-metric-value">{engineSize > 0 ? fmtDM(annualDist) : '—'}</div>
              <div className="ce-metric-sub">Engine size × {planPct}% planning rate</div>
            </div>
            <div className="ce-metric-card ce-metric-light">
              <div className="ce-metric-label">Monthly Distribution</div>
              <div className="ce-metric-value">{engineSize > 0 ? fmtDM(monthlyDist) : '—'}</div>
              <div className="ce-metric-sub">Deposited into PULS monthly</div>
            </div>
            <div className="ce-metric-card ce-metric-light">
              <div className="ce-metric-label">Cash Reserve Fund</div>
              <div className="ce-metric-value">PULS</div>
              <div className="ce-metric-sub">Ultra short-term bond ETF — cash equivalent with competitive yield</div>
            </div>
          </div>

          {/* Narrative */}
          <div className="ce-narrative-box">
            <p className="ce-narrative-p">
              Monthly income from the {tier ? 'notes and funds' : 'four funds'} flows into <strong>PULS</strong> — an ultra short-term bond fund with a competitively high yield for a cash-equivalent ETF. That cash accumulates throughout the year.
            </p>
            <p className="ce-narrative-p">
              At year-end, the accumulated cash covers the Roth conversion tax bill — <strong>no check written out of savings, no reduction in spendable assets.</strong>
            </p>
            <p className="ce-narrative-p">
              Any distributions above what is needed for taxes stay in PULS, building a growing reserve that reduces or eliminates out-of-pocket costs in future years.
            </p>
            {tier && (
              <p className="ce-narrative-p">
                The engine sleeve is modeled <strong>flat</strong> — your plan assumes the income pays the taxes and the principal
                simply holds its value. Any growth the notes or funds deliver beyond that is upside the plan never counted on.
              </p>
            )}
          </div>
        </CEPage>

        {/* ── PAGE 4: ASSUMPTIONS & DISCLOSURES ── */}
        <CEPage num={4} clientName={clientName}>
          <CESectionTitle>{tier ? 'Assumptions & Important Disclosures' : 'Important Disclosures'}</CESectionTitle>
          <div className="ce-disclosure-body">
            {tier && (
              <>
                <p className="ce-disclosure-section-title">Plan Assumptions</p>
                <p>
                  Engine planning rate: <strong>{planPct}% annual distribution</strong> ({tier.label} — {tier.sub}). The planning
                  rate is a distribution assumption, not a total-return assumption, and is deliberately set below the current
                  indicative note coupon of {ANCHOR_NOTE.coupon}. Engine principal is modeled flat: the plan assumes no growth on
                  the engine sleeve. Structured note terms are indicative as of the preparation date: worst-of S&P 500 / Nasdaq-100,
                  36-month term, monthly observation, {ANCHOR_NOTE.couponBarrier} coupon barrier, {ANCHOR_NOTE.principalBarrier} principal
                  barrier, issuer callable after 3 months. Actual terms are set at issuance and will vary; notes are laddered across
                  maturities and issuers.
                </p>
              </>
            )}
            <p>The projections contained in this report are based on client-provided inputs and reasonable assumptions believed to be accurate at the time of preparation. All values shown are hypothetical illustrations only and are intended for educational and planning purposes.</p>
            <p>No guarantee of future results is expressed or implied. Investment returns, tax rates, inflation, Social Security benefits, and other variables may differ materially from the assumptions used in this report. Actual outcomes will vary.</p>
            <p>This report does not constitute tax, legal, or investment advice. The scenarios presented are illustrative comparisons only and should not be construed as a recommendation to purchase any specific financial product or pursue any specific strategy.</p>
            <p>Roth conversion strategies involve complex tax considerations. The tax impact of any conversion depends on your total income, deductions, filing status, state tax laws, and future legislation, all of which are subject to change. Consult a qualified tax advisor or attorney before making any financial, tax, or investment decisions.</p>
            <p className="ce-disclosure-section-title">JBF Convert Engine — Important Notes</p>
            {tier ? (
              <>
                <p>Structured notes are unsecured obligations of the issuing bank and are subject to the issuer's credit risk. Coupons are contingent: if the worst-performing underlying index closes below the coupon barrier on an observation date, that coupon is not paid at that time (the Memory feature pays missed coupons only if the barrier is later recovered). If the worst-performing index has declined more than 40% at maturity, principal is reduced by the full extent of that index's decline. Issuer call features create reinvestment risk — replacement notes may carry lower coupons. Structured notes may have limited liquidity before maturity.</p>
                <p>The tax-funded conversion model assumes portfolio income is sufficient to cover Roth conversion tax costs during the conversion period. Actual income may be higher or lower than the planning rate. If income falls short, additional outside funds would be required to cover conversion taxes, which could reduce the projected legacy benefit.</p>
                <p>Covered-call ETF distribution rates cited (QYLD 12.4%, SPYI 11.9%, MLPD 13.9%, FEPI 25.2%) are trailing twelve-month figures as of the preparation date and are subject to change. A fund's distribution rate is not its total return; distributions may include return of capital, and covered-call strategies can experience NAV erosion over time. SPYI distributions may qualify for Section 1256 blended tax treatment; consult your tax advisor.</p>
              </>
            ) : (
              <>
                <p>The JBF Convert Engine scenario uses a 14% gross yield assumption (11% distributed, 3% retained for growth) based on the current JBF Income Portfolio objective. This yield assumption is for plan design purposes only — it is not guaranteed and may not be achieved. Portfolio distributions and total return will fluctuate based on market conditions, interest rates, dividend policy changes, and portfolio composition. Past distribution rates are not indicative of future results.</p>
                <p>The tax-funded conversion model assumes JBF Income Portfolio distributions are sufficient to cover Roth conversion tax costs during the conversion period. Actual distributions may be higher or lower than projected. If distributions fall short, additional outside funds would be required to cover conversion taxes, which could reduce the projected legacy benefit.</p>
                <p>The JBF Income Portfolio is a managed portfolio of monthly dividend ETFs designed to target high-yield distributions. Portfolio values fluctuate and past performance is not indicative of future results. Any stated yield, distribution rate, or return is a portfolio objective only — not a guarantee or promise of performance. JBF manages this portfolio on a discretionary basis.</p>
                <p>All fund yields cited (FEPI 25.6%, QYLD 12.2%, SPYI 12.0%, SLVO 42.3%) are trailing twelve-month figures as of the date of preparation and are subject to change. SLVO is an exchange-traded note (ETN) and carries both commodity (silver) price risk and issuer credit risk. SPYI distributions may qualify as Section 1256 contracts with favorable blended tax treatment; consult your tax advisor to confirm eligibility.</p>
              </>
            )}
            <p className="ce-disclosure-firm">J Bradin Financial — Prepared by Jesse Bradin. This report is confidential and intended solely for the use of the individual named herein.</p>
          </div>
        </CEPage>

      </div>
    </div>
  );
}
